export enum SubscriptionPeriod {
    Month = 'monthly',
    Year = 'yearly',
}

export enum SubscriptionPlanType {
    Core = 'core',
    Personal = 'personal',
    Team = 'team',
}

export type SubscriptionPlatform = 'android' | 'ios';

export type PlannedDowngrade = {
    created_at: string;
    current_plan_valid_until: string;
    effective_date: string;
    from_plan_id: string;
    new_number_of_seats: number;
    to_plan_id: string;
    upcoming_payment_date: string | null;
    upcoming_payment: UpcomingPayment | null;
    period: SubscriptionPeriod | null;
    platform: SubscriptionPlatform;
};

export type Subscription = {
    id: string;
    productId: string | null;
    status: SubscriptionStatus;
    platform: SubscriptionPlatform | null;
    upcomingPaymentDate: string | null;
    upcomingPayment: UpcomingPaymentAmount | null;
    validUntil: string | null;
    name: string;
    period: SubscriptionPeriod | null;
    organisationId: string;
    canceledAt: string | null;
    canceledBy: string | null;
    type: SubscriptionPlanType;
    currentPlan: string | null;
    plannedDowngrade: PlannedDowngrade | null;
    planSeats: number;
};

/**
 * Represents the possible states of a subscription in the application.
 * @property {string} ACTIVE - Subscription is active and valid
 * @property {string} INACTIVE - Subscription is revoked or terminated
 * @property {string} TRIAL - Subscription is in trial period
 * @property {string} SUSPENDED - Subscription is active based on validUntil date. It handles ON_HOLD and cancelled if validUntil is in the future.
 * If validUntil is in the past, it should be treated as invalid subscription.
 * @property {string} GRACE_PERIOD - Subscription is in grace period, treated as valid
 * @property {string} INTERNAL_TRIAL - Subscription is in internal trial period, treated as valid with no restrictions
 */
export enum SubscriptionStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    TRIAL = 'TRIAL',
    SUSPENDED = 'SUSPENDED',
    GRACE_PERIOD = 'GRACE_PERIOD',
    INTERNAL_TRIAL = 'INTERNAL_TRIAL',
}

export type UpcomingPaymentAmount = {
    currency_name: string;
    value: number;
};
export interface UpcomingPayment {
    date: string | null;
    amount: UpcomingPaymentAmount;
}

export interface SubscriptionResponse {
    id: string;
    organisation_id: string;
    payment_channel: SubscriptionPlatform | null;
    status: SubscriptionStatus;
    valid_until: string;
    canceled_at: string | null;
    canceled_by: string | null;
    current_plan: string | null;
    upcoming_payment: UpcomingPayment | null;
    product_id: string | null;
    planned_downgrade: PlannedDowngrade | null;
}

export enum SubscriptionPlanBillingPeriod {
    Monthly = 'monthly',
    Yearly = 'yearly',
}

export interface SubscriptionPlanResponse {
    billing_period: SubscriptionPlanBillingPeriod;
    created_at: number;
    currency_code: string;
    offer_id: string;
    plan_id: string;
    platform: string;
    price: string;
    product_id: string;
    region_code: string;
    seats: number;
    updated_at: number;
}

export type SubscriptionBenefit = {
    prefix?: string;
    text: string;
    suffix?: string;
};

export type SubscriptionPlanData = {
    platform: 'android' | 'ios';
    plan_id: string;
    seats: number;
    billing_period: SubscriptionPeriod;
    offer_id: string;
    price: string;
    region_code: string;
    currency_code: string;
    product_id: string;
};
